import { useState, useEffect, useContext } from "react";
import { useAtom, useAtomValue } from "jotai";

import customAxios from "@/custom/custom-axios";
import {
  TaskRpts,
  TaskRptFinal,
  V2taskData,
  Rptproblems
} from "@/types/owner/task/v2task";
import {
  memoSaveLoadingAtom,
  memoTargetAtom,
  memoUpdatedDataAtom
} from "@/states/owner/task/problems/memoAtom";
import { OWNER_END_POINTS } from "@/constants/owner/endPoints";
import { AuthContext } from "@/contexts/auth";

import type { MemoData } from "@/types/owner/task/memo";

/* 異常箇所メモ */
export const useProblemMemo = () => {
  const { token } = useContext(AuthContext);
  const [memoUpdatedData, setMemoUpdatedData] = useAtom(memoUpdatedDataAtom);
  const [isSaveLoading, setIsSaveLoading] = useAtom(memoSaveLoadingAtom);
  const memoTarget = useAtomValue(memoTargetAtom);
  const [taskData, setTaskData] = useState<V2taskData[]>([]);
  const [rptName, setRptName] = useState("");
  const [taskrpts, setTaskrpts] = useState<TaskRpts[]>([]);
  const [targetTaskrpt, setTargetTaskrpt] = useState<TaskRpts | null>(null); //選択されたタスクの異常箇所
  const [tasksubrptCommentsId, setTasksubrptCommentsId] = useState<number>(0); //選択されたタスクの異常箇所のコメントID
  const [taskRptFinals, setTaskRptFinals] = useState<TaskRptFinal[]>([]);
  const [problemMemo, setProblemMemo] = useState("");
  const [inputMemo, setInputMemo] = useState<string | null>(null);
  const [targetRptProblem, setTargetRptProblem] = useState<Rptproblems | null>(
    null
  );
  const [targetTaskrptFinal, setTargetTaskrptFinal] =
    useState<TaskRptFinal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [memoData, setMemoData] = useState<MemoData[]>([]);
  const [fetchFlag, setFetchFlag] = useState(false);
  const [newMemoText, setNewMemoText] = useState<string | null>(null);
  const [commentId, setCommentId] = useState<number>(0);

  //メモ表示を更新
  const updateMemoDisplay = (memoCommentId: number, resData: any) => {
    const updatedDataArr = memoUpdatedData?.filter(
      (data) => data.id !== memoCommentId
    );
    let updatedData: {
      id: number;
      comment: string;
    }[] = [];

    if (updatedDataArr) updatedData = updatedDataArr;
    updatedData?.push({
      id: Number(resData.id),
      comment: resData.comment
    });
    setMemoUpdatedData(updatedData);
  };

  //指定された異常箇所データをセット
  useEffect(() => {
    //選択されたタスクの異常箇所を取得->セット
    if (taskData.length > 0 && memoTarget) {
      //箇所名をセット
      setRptName(memoTarget.rptName || "");

      if (memoTarget.taskrptsId) {
        const targetTaskData = taskData
          .flatMap((task) => task?.taskrpts ?? [])
          .filter((taskrpt) => taskrpt.id === memoTarget.taskrptsId);
        setTaskrpts(targetTaskData || []);
      }

      //選択されたタスクのその他異常箇所を取得->セット
      if (memoTarget.taskRptFinalsId) {
        //チェックリストとその他異常箇所を判別
        const postType = memoTarget.taskRptFinalsProblemType.toLowerCase();
        const problemType = postType.includes("comment")
          ? "comments"
          : "rptproblemtypes";
        const isSelectedProblem = postType.includes("selectedproblem"); //異常チェックリストかどうか（報告確認画面からの場合）

        const targetTaskData = taskData
          .flatMap((task) => task?.taskrptfinals ?? [])
          .filter((taskRptFinal) =>
            isSelectedProblem
              ? taskRptFinal.rptproblems?.some(
                  (rptProblem) =>
                    rptProblem.problem_comment?.id ===
                    memoTarget.taskRptFinalsId
                )
              : taskRptFinal.id === memoTarget.taskRptFinalsId &&
                taskRptFinal.post_type === problemType
          );

        setTaskRptFinals(targetTaskData);
      }
      setFetchFlag(true); //メモコメントを取得fetchが何度も実行されないようにするフラグ
    }

    return () => {
      setTaskrpts([]);
      setTaskRptFinals([]);
    };
  }, [taskData, memoTarget]);

  //保存済みのメモコメントを取得
  useEffect(() => {
    (async () => {
      if (memoTarget?.taskId && memoData.length === 0 && fetchFlag) {
        try {
          setIsLoading(true);
          const apiUrl = `${OWNER_END_POINTS.TASK.BASE}/${memoTarget.taskId}/problem-comment`;
          const res = await customAxios.get(apiUrl, token);
          setMemoData(res.data);
          setFetchFlag(false);
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      setFetchFlag(false);
      setMemoData([]);
    };
  }, [memoTarget?.taskId, fetchFlag]);

  //メモコメントを保存
  useEffect(() => {
    (async () => {
      if (
        inputMemo !== null &&
        (targetTaskrpt || (targetRptProblem && targetTaskrptFinal))
      ) {
        //メモコメントIDを取得
        const firstSubrptWithComments = targetTaskrpt?.tasksubrpts?.find(
          (subrpt) => subrpt.post_type === "comments"
        ); //報告確認画面からの場合
        const problemCommentId = firstSubrptWithComments?.comments.find(
          (comment) => comment.id === tasksubrptCommentsId
        )?.problem_comment?.id; //登録一覧画面からの場合

        const memoCommentId = targetTaskrpt
          ? problemCommentId ||
            targetTaskrpt.tasksubrpts?.[0].comments.find(
              (comment) => comment.id === tasksubrptCommentsId
            )?.problem_comment?.id
          : targetRptProblem?.problem_comment?.id;

        //タスクIDを取得
        const taskId = targetTaskrpt?.task_id || targetTaskrptFinal?.task_id;

        //メモコメントを保存
        const apiUrl = `${OWNER_END_POINTS.TASK.BASE}/${taskId}/problem-comment/${memoCommentId}`;
        try {
          setIsSaveLoading(true);
          const res = await customAxios.put(
            apiUrl,
            { comment: inputMemo },
            token
          );
          setProblemMemo(res.data.comment);
          //メモ表示を更新
          if (memoCommentId) updateMemoDisplay(memoCommentId, res.data);
        } catch (error) {
          console.error(error);
        } finally {
          setInputMemo(null);
          setIsSaveLoading(false);
        }
      }
    })();
  }, [inputMemo, targetTaskrpt, targetRptProblem, targetTaskrptFinal]);

  //メモが更新されたら表示を更新
  useEffect(() => {
    if (memoUpdatedData) {
      const updatedMemo = memoUpdatedData.find((data) => data.id === commentId);
      if (updatedMemo) {
        setNewMemoText(updatedMemo.comment);
      }
    }
  }, [memoUpdatedData]);

  return {
    rptName,
    taskrpts,
    taskRptFinals,
    problemMemo,
    isLoading,
    isSaveLoading,
    memoData,
    newMemoText,
    setTaskData,
    setTargetTaskrpt,
    setTasksubrptCommentsId,
    setInputMemo,
    setTargetRptProblem,
    setTargetTaskrptFinal,
    setCommentId
  };
};
